<template>
  <div class="terms-conditions-page">
    <!-- Header Component -->
    <Header />

    <div class="terms-conditions">
      <div class="main-container">
        <!-- Header Section -->
        <div class="title-container">
          <div class="corner corner-tl"></div>
          <div class="corner corner-tr"></div>
          <div class="corner corner-bl"></div>
          <div class="corner corner-br"></div>
          <h1>Terms & Conditions</h1>
        </div>

        <!-- Agreement to Terms -->
        <div class="section-container">
          <div class="policy-section intro-section">
            <div class="section-header">
              <div class="header-left">
                <div class="icon">
                  <CheckCircleIcon class="check-icon" />
                </div>
                <h2>Agreement to Terms</h2>
              </div>
            </div>
            <p>
              Welcome to ElevatED, a web-based academic tutoring platform designed to connect Senior
              High School students with qualified tutors. By accessing or using our platform, you
              agree to be bound by these Terms and Conditions ("Terms").
            </p>
            <p>
              These Terms constitute a legally binding agreement between you and ElevatED. If you do
              not agree to these Terms, please do not use our platform.
            </p>

            <div class="info-box">
              <p><strong>Last Updated:</strong> October 2025</p>
              <p><strong>Effective Date:</strong> October 2025</p>
              <p><strong>Governing Law:</strong> Republic of the Philippines</p>
            </div>
          </div>
        </div>

        <!-- Platform Overview -->
        <div class="section-container">
          <div class="policy-section">
            <div class="section-header">
              <div class="header-left">
                <div class="icon">
                  <BookOpenIcon class="book-icon" />
                </div>
                <h2>Platform Overview</h2>
              </div>
            </div>
            <p>
              ElevatED is an educational platform that facilitates connections between Senior High
              School students and qualified tutors. Our services include:
            </p>

            <div class="services-grid">
              <div class="service-column">
                <h3>For Students:</h3>
                <ul class="service-list">
                  <li>Smart tutor matching and recommendations</li>
                  <li>Online tutoring session scheduling</li>
                  <li>AI-powered study material generation</li>
                  <li>Progress tracking and feedback systems</li>
                  <li>Access to qualified peer and professional tutors</li>
                </ul>
              </div>
              <div class="service-column">
                <h3>For Tutors:</h3>
                <ul class="service-list">
                  <li>Opportunity to share knowledge and expertise</li>
                  <li>Flexible scheduling and availability management</li>
                  <li>Student feedback and rating systems</li>
                  <li>Recognition and certification opportunities</li>
                  <li>Professional development and networking</li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        <!-- User Accounts and Registration -->
        <div class="section-container">
          <div class="policy-section">
            <div class="section-header">
              <div class="header-left">
                <div class="icon">
                  <UserIcon class="user-icon" />
                </div>
                <h2>User Accounts and Registration</h2>
              </div>
            </div>

            <h3>Account Creation</h3>
            <ul class="account-requirements">
              <li>You must be at least 16 years old to create an account</li>
              <li>You must provide accurate and complete information during registration</li>
              <li>Email verification is required for all accounts</li>
              <li>One account per person; multiple accounts are prohibited</li>
            </ul>

            <h3>Student Verification</h3>
            <div class="verification-box">
              <p class="verification-title">Required Documents:</p>
              <ul class="verification-list">
                <li>Clear photo of valid school ID</li>
                <li>Selfie holding the school ID</li>
                <li>Proof of current enrollment (if requested)</li>
              </ul>
            </div>

            <h3>Tutor Application Process</h3>
            <div class="application-stages">
              <div class="stage">
                <div class="stage-header">
                  <span class="stage-number">Stage 1</span>
                  <span class="stage-title">Document Submission</span>
                </div>
                <ul class="stage-requirements">
                  <li>Transcript of Records (TOR)</li>
                  <li>Academic certificates and diplomas</li>
                  <li>Curriculum Vitae (CV)</li>
                  <li>Barangay Clearance or NBI Clearance</li>
                  <li>Signed tutor agreement contract</li>
                  <li>Optional introductory video</li>
                </ul>
              </div>
              <div class="stage">
                <div class="stage-header">
                  <span class="stage-number">Stage 2</span>
                  <span class="stage-title">External Validation</span>
                </div>
                <ul class="stage-requirements">
                  <li>Review by qualified external validators</li>
                  <li>Assessment of teaching capability</li>
                  <li>Interview process (if required)</li>
                  <li>Final document verification</li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        <!-- Tutoring Session Policies -->
        <div class="section-container">
          <div class="policy-section">
            <div class="section-header">
              <div class="header-left">
                <div class="icon">
                  <CalendarDaysIcon class="calendar-icon" />
                </div>
                <h2>Tutoring Session Policies</h2>
              </div>
            </div>

            <h3>Scheduling Appointments</h3>
            <ul class="scheduling-list">
              <li>
                <ClockIcon class="clock-icon" />Sessions are conducted online through the platform
              </li>
              <li>
                <ClockIcon class="clock-icon" />Students can schedule appointments based on tutor
                availability
              </li>
              <li>
                <ClockIcon class="clock-icon" />Tutors must confirm appointments within 24 hours
              </li>
              <li>
                <ClockIcon class="clock-icon" />Session confirmations are sent via email and
                platform notifications
              </li>
            </ul>

            <h3>Cancellation and Rescheduling</h3>
            <div class="policy-boxes">
              <div class="policy-box cancellation">
                <h4>Cancellation Policy</h4>
                <ul class="policy-list">
                  <li>Must cancel at least 12 hours before session</li>
                  <li>Late cancellations may affect user ratings</li>
                  <li>Repeated cancellations may result in restrictions</li>
                  <li>Emergency cancellations will be reviewed case-by-case</li>
                </ul>
              </div>
              <div class="policy-box no-show">
                <h4>No-Show Policy</h4>
                <ul class="policy-list">
                  <li>15-minute grace period after scheduled start time</li>
                  <li>Either party may cancel after 15 minutes</li>
                  <li>No-shows are recorded and may affect ratings</li>
                  <li>Repeated no-shows may result in account restrictions</li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        <!-- User Responsibilities and Conduct -->
        <div class="section-container">
          <div class="policy-section">
            <div class="section-header">
              <div class="header-left">
                <div class="icon">
                  <ShieldExclamationIcon class="shield-icon" />
                </div>
                <h2>User Responsibilities and Conduct</h2>
              </div>
            </div>

            <h3>General Conduct</h3>
            <div class="conduct-grid">
              <div class="conduct-column">
                <h4 class="acceptable-title">
                  <CheckIcon class="check-icon-green" />Acceptable Behavior
                </h4>
                <ul class="conduct-list">
                  <li>Respectful and professional communication</li>
                  <li>Punctuality for scheduled sessions</li>
                  <li>Honest and constructive feedback</li>
                  <li>Compliance with academic integrity</li>
                  <li>Appropriate use of platform features</li>
                </ul>
              </div>
              <div class="conduct-column">
                <h4 class="prohibited-title">
                  <XMarkIcon class="x-icon-red" />Prohibited Behavior
                </h4>
                <ul class="conduct-list">
                  <li>Harassment, discrimination, or bullying</li>
                  <li>Sharing inappropriate or offensive content</li>
                  <li>Attempting to circumvent platform policies</li>
                  <li>Providing false information or credentials</li>
                  <li>Unauthorized sharing of session content</li>
                </ul>
              </div>
            </div>

            <h3>Academic Integrity</h3>
            <div class="academic-integrity-box">
              <p class="integrity-title">
                Important: ElevatED is designed to support learning, not to facilitate academic
                dishonesty.
              </p>
              <ul class="integrity-list">
                <li>Tutoring should focus on understanding concepts, not completing assignments</li>
                <li>
                  Students are responsible for following their school's academic integrity policies
                </li>
                <li>Tutors should not complete homework or exams for students</li>
                <li>Any suspected academic dishonesty will be reported to relevant authorities</li>
              </ul>
            </div>
          </div>
        </div>

        <!-- Feedback and Rating System -->
        <div class="section-container">
          <div class="policy-section">
            <div class="section-header">
              <div class="header-left">
                <div class="icon">
                  <StarIcon class="star-icon" />
                </div>
                <h2>Feedback and Rating System</h2>
              </div>
            </div>

            <p class="section-description">
              Our feedback system ensures quality and continuous improvement of tutoring services:
            </p>

            <div class="feedback-grid">
              <div class="feedback-box">
                <h3>Rating Requirements</h3>
                <ul class="feedback-list">
                  <li>Both parties must rate each other after each session</li>
                  <li>Ratings are on a 1-5 star scale</li>
                  <li>Written feedback is encouraged but optional</li>
                  <li>Ratings become visible after both parties submit</li>
                </ul>
              </div>
              <div class="feedback-box">
                <h3>Quality Assurance</h3>
                <ul class="feedback-list">
                  <li>Consistently low ratings may result in review</li>
                  <li>Tutors with high ratings receive priority in matching</li>
                  <li>Feedback helps improve platform algorithms</li>
                  <li>Inappropriate feedback will be moderated</li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        <!-- Intellectual Property and Content -->
        <div class="section-container">
          <div class="policy-section">
            <div class="section-header">
              <div class="header-left">
                <div class="icon">
                  <DocumentIcon class="document-icon" />
                </div>
                <h2>Intellectual Property and Content</h2>
              </div>
            </div>

            <h3>Platform Content</h3>
            <p class="content-description">
              All content on the ElevatED platform, including but not limited to text, graphics,
              logos, software, and AI-generated materials, is owned by ElevatED or its licensors and
              is protected by intellectual property laws.
            </p>

            <h3>User-Generated Content</h3>
            <ul class="content-list">
              <li>You retain ownership of content you create and upload</li>
              <li>You grant ElevatED a license to use your content for platform operations</li>
              <li>You are responsible for ensuring you have rights to any content you share</li>
              <li>ElevatED may remove content that violates these Terms</li>
            </ul>
          </div>
        </div>

        <!-- Limitation of Liability -->
        <div class="section-container">
          <div class="policy-section">
            <div class="section-header">
              <div class="header-left">
                <div class="icon">
                  <ExclamationTriangleIcon class="warning-icon" />
                </div>
                <h2>Limitation of Liability</h2>
              </div>
            </div>

            <div class="liability-disclaimer-box">
              <h3 class="disclaimer-title">Important Disclaimers</h3>
              <ul class="disclaimer-list">
                <li>ElevatED is a platform that facilitates connections between users</li>
                <li>We do not guarantee specific academic outcomes or improvements</li>
                <li>The quality of tutoring depends on individual tutors, not the platform</li>
                <li>AI-generated content may contain errors and should be verified</li>
                <li>Users are responsible for their own academic decisions and outcomes</li>
              </ul>
            </div>

            <p class="liability-statement">
              To the maximum extent permitted by law, ElevatED shall not be liable for any indirect,
              incidental, special, consequential, or punitive damages arising from your use of the
              platform.
            </p>
          </div>
        </div>

        <!-- Account Termination -->
        <div class="section-container">
          <div class="policy-section">
            <div class="section-header">
              <div class="header-left">
                <div class="icon">
                  <ScissorsIcon class="scissors-icon" />
                </div>
                <h2>Account Termination</h2>
              </div>
            </div>

            <div class="termination-grid">
              <div class="termination-column">
                <h3>Voluntary Termination</h3>
                <ul class="termination-list">
                  <li>You may delete your account at any time</li>
                  <li>Account deletion is permanent and irreversible</li>
                  <li>Some data may be retained for legal compliance</li>
                  <li>Outstanding sessions must be completed or cancelled</li>
                </ul>
              </div>
              <div class="termination-column">
                <h3>Involuntary Termination</h3>
                <ul class="termination-list">
                  <li>Violation of Terms may result in account suspension</li>
                  <li>Repeated violations may lead to permanent ban</li>
                  <li>Fraudulent activity results in immediate termination</li>
                  <li>Appeals process available for disputed terminations</li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        <!-- Dispute Resolution -->
        <div class="section-container">
          <div class="policy-section">
            <div class="section-header">
              <div class="header-left">
                <div class="icon">
                  <EnvelopeIcon class="envelope-icon" />
                </div>
                <h2>Dispute Resolution</h2>
              </div>
            </div>
            <p class="dispute-description">
              Any disputes arising from these Terms shall be resolved through good faith
              negotiation. If resolution cannot be reached, disputes shall be subject to the
              jurisdiction of Philippine courts and governed by Philippine law.
            </p>
          </div>
        </div>

        <!-- Changes to Terms -->
        <div class="section-container">
          <div class="policy-section">
            <div class="section-header">
              <div class="header-left">
                <h2>Changes to Terms</h2>
              </div>
            </div>

            <p class="changes-description">
              We reserve the right to modify these Terms at any time. Changes will be effective
              immediately upon posting on the platform. We will notify users of material changes
              through:
            </p>

            <ul class="notification-list">
              <li>Email notifications to all registered users</li>
              <li>Prominent notices on the platform homepage</li>
              <li>In-app notifications upon login</li>
            </ul>

            <p class="acceptance-statement">
              Your continued use of ElevatED after changes are posted constitutes acceptance of the
              updated Terms.
            </p>
          </div>
        </div>
      </div>
    </div>

    <!-- Footer Component -->
    <Footer />
  </div>
</template>

<script setup>
import {
  CheckCircleIcon,
  BookOpenIcon,
  UserIcon,
  CalendarDaysIcon,
  ClockIcon,
  ShieldExclamationIcon,
  CheckIcon,
  XMarkIcon,
  StarIcon,
  DocumentIcon,
  ExclamationTriangleIcon,
  ScissorsIcon,
  EnvelopeIcon,
} from "@heroicons/vue/24/outline";
import Header from "./Header.vue";
import Footer from "./Footer.vue";
</script>

<style scoped>
/* Bring headings closer to their icons */
.section-header {
  display: flex;
  align-items: center;
  gap: 0.25rem;
}

.section-header .icon {
  width: 24px;
  height: 24px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.section-header .icon svg {
  width: 20px;
  height: 20px;
}

.section-header h2 {
  margin: 0;
  line-height: 1.1;
}

.terms-conditions-page {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
}

.terms-conditions {
  flex: 1;
  background: transparent;
  padding: 3rem 1rem;
  font-family: "Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
  display: flex;
  justify-content: center;
  align-items: flex-start;
  margin-top: 150px; /* Increased margin to account for fixed header */
}

.main-container {
  max-width: 760px;
  width: 100%;
  margin: 0 auto;
  padding: 0;
}

/* Title Container with Square Border */
.title-container {
  display: block;
  position: relative;
  padding: 1rem 2rem;
  width: 100%;
  margin: 0 auto 3rem auto;
  text-align: center;
}

/* Square border */
.title-container::before {
  content: "";
  position: absolute;
  top: -12px;
  left: 0;
  right: 0;
  bottom: -12px;
  border: 3px solid #5271ff;
  border-radius: 0;
  background: transparent;
}

/* Corner squares */
.corner {
  position: absolute;
  width: 14px;
  height: 14px;
  background: #5271ff;
}

.corner-tl {
  top: -18px;
  left: -6px;
}
.corner-tr {
  top: -18px;
  right: -6px;
}
.corner-bl {
  bottom: -18px;
  left: -6px;
}
.corner-br {
  bottom: -18px;
  right: -6px;
}

h1 {
  color: #5271ff;
  font-size: 3.25rem;
  font-weight: 700;
  margin: 0;
  position: relative;
  z-index: 1;
  white-space: nowrap;
}

/* Section Containers */
.section-container {
  margin: 1.25rem 0;
}

.policy-section {
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  padding: 1.25rem 1.5rem;
  box-shadow: 0 12px 28px rgba(0, 0, 0, 0.12), 0 4px 10px rgba(0, 0, 0, 0.06);
}

.intro-section {
  background: #ffffff;
}

.section-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 0.5rem;
  padding: 0.5rem 0;
}

.header-left {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.icon {
  width: 24px;
  height: 24px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.check-icon {
  width: 20px;
  height: 20px;
  color: #5271ff;
}

/* Typography */
h2 {
  color: #2d3748;
  font-size: 1.25rem;
  font-weight: 700;
  margin: 0;
}

h3 {
  color: #000000;
  font-size: 1rem;
  font-weight: 600;
  margin-bottom: 0.5rem;
  margin-top: 1rem;
}

h4 {
  color: #2d3748;
  font-size: 0.9rem;
  font-weight: 600;
  margin-bottom: 0.5rem;
  margin-top: 0.5rem;
}

p {
  color: #6b7280;
  line-height: 1.4;
  margin-bottom: 0.5rem;
  font-size: 0.9rem;
  font-weight: 400;
}

/* Lists */
ul {
  margin: 1rem 0;
  padding-left: 0;
  list-style: none;
}

li {
  color: #4a5568;
  margin-bottom: 0.8rem;
  padding-left: 1.5rem;
  position: relative;
  font-size: 0.9rem;
  line-height: 1.6;
  font-weight: 400;
}

li::before {
  content: "•";
  color: transparent;
  font-weight: 600;
  position: absolute;
  left: 0;
  font-size: 1.2em;
  line-height: 0.8;
}

strong {
  font-weight: 700;
  color: #2d3748;
}

/* Info Box */
.info-box {
  background: #e8f4ff;
  border-left: 4px solid #4a90e2;
  padding: 1rem;
  margin-top: 1.5rem;
}

.info-box p {
  margin: 0.25rem 0;
  font-size: 0.9rem;
  color: #2b6cb0;
}

.info-box p:first-child {
  margin-top: 0;
}

.info-box p:last-child {
  margin-bottom: 0;
}

.info-box strong {
  color: #1e4e8c;
  font-weight: 600;
  margin-right: 0.5rem;
}

/* Warning Box */
.purple-dots li {
  position: relative;
  padding-left: 1.5rem;
  margin-bottom: 0.75rem;
  color: #6b7280;
  line-height: 1.5;
}

.purple-dots li::before {
  content: "";
  position: absolute;
  left: 0;
  top: 0.6em;
  width: 8px;
  height: 8px;
  background: #5271ff;
  border-radius: 50%;
}

.user-icon {
  width: 24px;
  height: 24px;
  color: #8b5cf6;
}

.account-requirements {
  list-style: none;
  padding: 0;
  margin: 0.75rem 0;
}

.account-requirements li {
  color: #6b7280;
  font-size: 0.875rem;
  line-height: 1.4;
  margin-bottom: 0.5rem;
  padding-left: 1.25rem;
  position: relative;
}

.account-requirements li::before {
  content: "";
  position: absolute;
  left: 0;
  top: 0.5rem;
  width: 0.5rem;
  height: 0.5rem;
  background-color: #8b5cf6;
  border-radius: 50%;
}

.verification-box {
  background: #fffbeb;
  padding: 1rem;
  margin: 0.75rem 0;
}

.verification-title {
  color: #92400e;
  font-size: 0.875rem;
  font-weight: 600;
  margin-bottom: 0.5rem;
}

.verification-list {
  list-style: none;
  padding: 0;
  margin: 0;
}

.verification-list li {
  color: #92400e;
  font-size: 0.875rem;
  line-height: 1.4;
  margin-bottom: 0.375rem;
  padding-left: 0.75rem;
  position: relative;
}

.verification-list li::before {
  content: "•";
  position: absolute;
  left: 0;
  color: #92400e;
}

.application-stages {
  margin: 0.75rem 0;
}

.stage {
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 0.75rem;
  padding: 1rem;
  margin-bottom: 0.75rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.stage:last-child {
  margin-bottom: 0;
}

.stage-header {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 0.75rem;
}

.stage-number {
  background: #f0f9ff;
  border: 1px solid #bae6fd;
  color: #000000;
  padding: 0.25rem 0.75rem;
  border-radius: 9999px;
  font-size: 0.75rem;
  font-weight: 600;
}

.stage:nth-child(2) .stage-number {
  background: #f0fdf4;
  border: 1px solid #bbf7d0;
  color: #000000;
}

.stage-title {
  font-size: 0.875rem;
  font-weight: 600;
  color: #111827;
}

.stage-requirements {
  list-style: none;
  padding: 0;
  margin: 0;
}

.stage-requirements li {
  color: #000000;
  font-size: 0.875rem;
  line-height: 1.4;
  margin-bottom: 0.375rem;
  padding-left: 0.75rem;
  position: relative;
}

.stage-requirements li::before {
  content: "•";
  position: absolute;
  left: 0;
  color: #9ca3af;
}

.stage-requirements li:last-child {
  margin-bottom: 0;
}

.calendar-icon {
  width: 24px;
  height: 24px;
  color: #f97316;
}

.scheduling-list {
  list-style: none;
  padding: 0;
  margin: 0.75rem 0;
}

.scheduling-list li {
  color: #6b7280;
  font-size: 0.875rem;
  line-height: 1.4;
  margin-bottom: 0.75rem;
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
}

.clock-icon {
  width: 16px;
  height: 16px;
  color: #f97316;
  flex-shrink: 0;
  margin-top: 0.125rem;
}

.policy-boxes {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1rem;

  margin-top: 0.75rem;
}

.policy-box {
  padding: 1rem;
  border-radius: 0.5rem;
  border: 0.5px solid rgb(199, 199, 199);
}

.policy-box.cancellation {
  background: #fef2f2;
}

.policy-box.no-show {
  background: #eff6ff;
}

.policy-box h4 {
  color: #dc2626;
  font-size: 0.875rem;
  font-weight: 600;
  margin-bottom: 0.75rem;
}

.policy-box.no-show h4 {
  color: #2563eb;
}

.policy-list {
  list-style: none;
  padding: 0;
  margin: 0;
}

.policy-list li {
  color: #6b7280;
  font-size: 0.875rem;
  line-height: 1.4;
  margin-bottom: 0.375rem;
  padding-left: 0.75rem;
  position: relative;
}

.policy-list li::before {
  content: "•";
  position: absolute;
  left: 0;
  color: #6b7280;
}

.policy-list li:last-child {
  margin-bottom: 0;
}

@media (max-width: 640px) {
  .policy-boxes {
    grid-template-columns: 1fr;
  }
}

.application-steps {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 1.5rem;
  margin-top: 1rem;
}

.step-box {
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 1.25rem;
}

.step-box h4 {
  color: #111827;
  font-size: 1rem;
  font-weight: 600;
  margin-bottom: 1rem;
}

.step-box ul {
  list-style: none;
  padding: 0;
  margin: 0;
}

.step-box li {
  color: #6b7280;
  margin-bottom: 0.5rem;
  padding-left: 1rem;
  position: relative;
  font-size: 0.9rem;
}

.step-box li::before {
  content: "•";
  position: absolute;
  left: 0;
  color: #9ca3af;
}

/* Grid Layouts */
.services-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1.5rem;
  margin-top: 1rem;
}

.service-column h3 {
  color: #111827;
  font-size: 1rem;
  font-weight: 600;
  margin-bottom: 0.75rem;
}

.service-list {
  list-style: none;
  padding: 0;
  margin: 0;
}

.service-list li {
  color: #6b7280;
  font-size: 0.875rem;
  line-height: 1.4;
  margin-bottom: 0.5rem;
  padding-left: 1rem;
  position: relative;
}

.service-list li::before {
  content: "•";
  position: absolute;
  left: 0;
  color: #6b7280;
}

.book-icon {
  width: 24px;
  height: 24px;
  color: #22c55e;
}

@media (max-width: 640px) {
  .services-grid {
    grid-template-columns: 1fr;
    gap: 1.5rem;
  }
}

.three-column {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 1.5rem;
  margin-top: 1rem;
}

@media (min-width: 768px) {
  .three-column {
    grid-template-columns: repeat(3, 1fr);
  }
}

.step-column {
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  padding: 1.25rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.column {
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  padding: 1rem 1.25rem;
}

.column h3,
.column h4 {
  color: #111827;
  font-weight: 700;
  margin-bottom: 0.5rem;
  font-size: 0.95rem;
  margin-top: 0;
}

.column ul {
  margin: 0;
}

.column li {
  color: #4b5563;
  margin-bottom: 0.4rem;
  padding-left: 1rem;
  line-height: 1.35;
  font-size: 0.85rem;
}

.column li::before {
  content: "•";
  color: #5271ff;
  position: absolute;
  left: 0;
  font-size: 1em;
  line-height: 0.8;
}

/* Feedback and Rating System */
.star-icon {
  width: 24px;
  height: 24px;
  color: #eab308;
}

.section-description {
  color: #6b7280;
  margin-bottom: 1rem;
  font-family: "Inter", sans-serif;
  font-size: 0.875rem;
  line-height: 1.4;
}

.feedback-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1rem;
}

.feedback-box {
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 0.5rem;
  padding: 1rem;
}

.feedback-box h3 {
  color: #111827;
  font-size: 1rem;
  font-weight: 600;
  margin-bottom: 0.75rem;
  font-family: "Inter", sans-serif;
}

.feedback-list {
  list-style: none;
  padding: 0;
  margin: 0;
}

.feedback-list li {
  color: #6b7280;
  font-size: 0.875rem;
  line-height: 1.4;
  margin-bottom: 0.5rem;
  padding-left: 1rem;
  position: relative;
  font-family: "Inter", sans-serif;
}

.feedback-list li::before {
  content: "•";
  position: absolute;
  left: 0;
  color: #6b7280;
}

/* Intellectual Property and Content */
.document-icon {
  width: 24px;
  height: 24px;
  color: #6366f1;
}

.content-description {
  color: #6b7280;
  font-size: 0.875rem;
  line-height: 1.5;
  margin-bottom: 1.5rem;
}

.content-list {
  list-style: none;
  padding: 0;
  margin: 0;
}

.content-list li {
  color: #6b7280;
  font-size: 0.875rem;
  line-height: 1.5;
  margin-bottom: 0.75rem;
  padding-left: 1rem;
  position: relative;
}

.content-list li::before {
  content: "•";
  position: absolute;
  left: 0;
  color: #6b7280;
}

/* Mobile Responsiveness */
@media (max-width: 768px) {
  .terms-conditions {
    padding: 1rem;
    margin-top: 120px; /* Adjusted margin for mobile view */
  }

  .main-container {
    padding: 0;
  }

  h1 {
    font-size: 2.5rem;
    white-space: normal;
  }

  .title-container {
    padding: 0.25rem 1rem;
  }

  .policy-section {
    padding: 1rem;
  }

  .two-column,
  .three-column {
    grid-template-columns: 1fr;
    gap: 1rem;
  }

  .section-header {
    flex-direction: row;
    align-items: center;
  }

  .icon {
    width: 28px;
    height: 28px;
  }

  .icon svg {
    width: 18px;
    height: 18px;
  }
}

@media (max-width: 480px) {
  .terms-conditions {
    margin-top: 100px; /* Further adjusted margin for smaller devices */
  }

  h1 {
    font-size: 2rem;
  }

  .corner {
    width: 12px;
    height: 12px;
  }

  .corner-tl {
    top: -16px;
    left: -8px;
  }
  .corner-tr {
    top: -16px;
    right: -8px;
  }
  .corner-bl {
    bottom: -16px;
    left: -8px;
  }
  .corner-br {
    bottom: -16px;
    right: -8px;
  }

  .title-container::before {
    top: -10px;
    bottom: -10px;
    border-width: 2px;
  }
}

/* User Responsibilities and Conduct Styles */
.shield-icon {
  width: 24px;
  height: 24px;
  color: #dc2626;
}

.conduct-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 2rem;
  margin: 1rem 0;
}

.conduct-column {
  padding: 0;
}

.acceptable-title,
.prohibited-title {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.875rem;
  font-weight: 600;
  margin-bottom: 1rem;
}

.acceptable-title {
  color: #22c55e;
}

.prohibited-title {
  color: #dc2626;
}

.check-icon-green {
  width: 16px;
  height: 16px;
  color: #22c55e;
}

.x-icon-red {
  width: 16px;
  height: 16px;
  color: #dc2626;
}

.conduct-list {
  list-style: none;
  padding: 0;
  margin: 0;
}

.conduct-list li {
  color: #000000;
  font-size: 0.875rem;
  line-height: 1.4;
  margin-bottom: 0.5rem;
  padding-left: 1rem;
  position: relative;
}

.conduct-list li::before {
  content: "•";
  position: absolute;
  left: 0;
  color: #000000;
}

.academic-integrity-box {
  background: #fffbeb;
  border-left: 4px solid #f59e0b;
  padding: 1rem;
  margin-top: 1rem;
}

.integrity-title {
  color: #92400e;
  font-weight: 600;
  margin-bottom: 0.75rem;
}

.integrity-list {
  list-style: none;
  padding: 0;
  margin: 0;
}

.integrity-list li {
  color: #92400e;
  font-size: 0.875rem;
  line-height: 1.4;
  margin-bottom: 0.5rem;
  padding-left: 0;
}

/* Limitation of Liability */
.warning-icon {
  width: 24px;
  height: 24px;
  color: #dc2626;
}

.liability-disclaimer-box {
  background: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: 0.5rem;
  padding: 1rem;
  margin: 1rem 0;
}

.disclaimer-title {
  color: #dc2626;
  font-size: 0.875rem;
  font-weight: 600;
  margin-bottom: 0.75rem;
  font-family: "Inter", sans-serif;
}

.disclaimer-list {
  list-style: none;
  padding: 0;
  margin: 0;
}

.disclaimer-list li {
  color: #dc2626;
  font-size: 0.875rem;
  line-height: 1.4;
  margin-bottom: 0.5rem;
  padding-left: 1rem;
  position: relative;
  font-family: "Inter", sans-serif;
}

.disclaimer-list li::before {
  content: "•";
  position: absolute;
  left: 0;
  color: #dc2626;
}

.liability-statement {
  color: #6b7280;
  font-size: 0.875rem;
  line-height: 1.5;
  margin-top: 1rem;
  font-family: "Inter", sans-serif;
}

/* Account Termination */
.scissors-icon {
  width: 24px;
  height: 24px;
  color: #7c3aed;
}

.termination-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1rem;
  margin-top: 0.75rem;
}

.termination-column h3 {
  color: #111827;
  font-size: 1rem;
  font-weight: 600;
  margin-bottom: 0.5rem;
  font-family: "Inter", sans-serif;
}

.termination-list {
  list-style: none;
  padding: 0;
  margin: 0;
}

.termination-list li {
  color: #6b7280;
  font-size: 0.875rem;
  line-height: 1.3;
  margin-bottom: 0.5rem;
  padding-left: 1rem;
  position: relative;
  font-family: "Inter", sans-serif;
  min-height: 1.2rem;
  display: flex;
  align-items: flex-start;
}

.termination-list li::before {
  content: "•";
  position: absolute;
  left: 0;
  color: #6b7280;
}

/* Dispute Resolution */
.envelope-icon {
  width: 24px;
  height: 24px;
  color: #22c55e;
}

.dispute-description {
  color: #6b7280;
  font-size: 0.875rem;
  line-height: 1.5;
  font-family: "Inter", sans-serif;
}

/* Changes to Terms */
.changes-description {
  color: #6b7280;
  font-size: 0.875rem;
  line-height: 1.5;
  margin-bottom: 1rem;
  font-family: "Inter", sans-serif;
}

.notification-list {
  list-style: none;
  padding: 0;
  margin: 1rem 0;
}

.notification-list li {
  color: #6b7280;
  font-size: 0.875rem;
  line-height: 1.4;
  margin-bottom: 0.5rem;
  padding-left: 1rem;
  position: relative;
  font-family: "Inter", sans-serif;
}

.notification-list li::before {
  content: "•";
  position: absolute;
  left: 0;
  color: #6b7280;
}

.acceptance-statement {
  color: #6b7280;
  font-size: 0.875rem;
  line-height: 1.5;
  margin-top: 1rem;
  font-family: "Inter", sans-serif;
}
</style>
